/**
 * What this company calls its two operating modes.
 *
 * 'normal' and 'cat' remain the stored identifiers — they are written into every assignment
 * and every mode period — but nothing a person reads should say them. Every caption goes
 * through here, so a company that renamed Group to "Storm" sees "Storm" on the board, the
 * wall, the settings sidebar and its email, with no scheduling data touched.
 *
 * Defaults must match MODE_DEFAULTS in server/src/repos/modes.js. The server is the source
 * of truth and always sends a resolved name; these cover the first paint before /api/me has
 * landed, and any view rendered without a session.
 */
const MODE_FALLBACK = { normal: 'Team', cat: 'Group' }

/** The tenant's word for one mode. `modes` is the map from /api/me or the wall payload. */
export function nameOf(modes, mode) {
  return modes?.[mode]?.name || MODE_FALLBACK[mode] || mode
}

/** "Storm mode" — for the sentences that need the noun rather than a bare badge. */
export function phraseOf(modes, mode) {
  return `${nameOf(modes, mode)} mode`
}

/**
 * Enabled unless the server has explicitly said otherwise.
 *
 * `!== false` rather than `=== true` on purpose, and the reason is the first paint: before
 * /api/me lands there is no map at all, and treating that as "everything off" would blank
 * the mode switch and half the settings sidebar for a frame. Written once here because four
 * copies of a defaulting rule is four chances for one of them to drift.
 */
export function isEnabled(modes, mode) {
  return modes?.[mode]?.enabled !== false
}

/** Only the modes this company actually uses, in board order. */
export function enabledModes(modes) {
  return ['normal', 'cat'].filter((m) => isEnabled(modes, m))
}
